import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';

import {
  getUserDetails,
  logout,
  reconcileUserInfoFromProfile,
  userSendMail,
  validateUserProfile,
} from '../../actions/userActions';
import {
  USER_SEND_MAIL_PROFILE_RESET,
  USER_VALIDATE_PROFILE_RESET,
} from '../../constants/userConstants';
import {
  selectIdentity,
  selectIsAuthed,
  selectUserInfo,
} from '../../selectors/userSelectors';
import { resolvePostVerificationDestination } from '../../utils/onboardingDestination';
import api from '../../api/axios';
import OtpCodeInput, { isCompleteOtpCode } from '../../components/OtpCodeInput';

const RESEND_COOLDOWN_SECONDS = 30;

const EMAIL_HYDRATION_ERROR =
  'Не вдалося завантажити адресу електронної пошти. Спробуйте ще раз.';

const EMAIL_RECONCILIATION_ERROR =
  'Не вдалося синхронізувати стан підтвердження пошти. Спробуйте ще раз.';

/** Phase-5 canonical email source: legacy selectUserInfo only. */
function readCanonicalEmail(userInfo) {
  return String(userInfo?.email || '').trim();
}

function isRequestCanceled(error) {
  return error?.name === 'CanceledError' || error?.code === 'ERR_CANCELED';
}

function ProfileEmailValidateScreen() {
  const [code, setCode] = useState('');
  const [resendCooldown, setResendCooldown] = useState(0);
  // statusSource owns which control an error belongs to:
  // 'send' | 'confirm' | null — never mark code invalid for send-mail errors.
  const [statusMessage, setStatusMessage] = useState({
    tone: 'idle',
    text: '',
    source: null,
  });
  const [codeSentOnce, setCodeSentOnce] = useState(false);

  const dispatch = useDispatch();
  const navigate = useNavigate();

  const isAuthed = useSelector(selectIsAuthed);
  const userInfo = useSelector(selectUserInfo);
  // identity is routing-only (isEmailVerified); email is never read from here.
  const identity = useSelector(selectIdentity);

  const {
    loading: loadingSendMail,
    error: errorSendMail,
    success: successSendMail,
  } = useSelector((state) => state.userSendMail);

  const {
    loading: loadingValidate,
    error: errorValidate,
    success: successValidate,
  } = useSelector((state) => state.validateUserProfile);

  const seededEmail = isAuthed ? readCanonicalEmail(userInfo) : '';
  const [resolvedEmail, setResolvedEmail] = useState(seededEmail);
  const [emailLoadState, setEmailLoadState] = useState(
    !isAuthed ? 'idle' : seededEmail ? 'ready' : 'loading',
  );
  const [emailLoadError, setEmailLoadError] = useState('');
  const [hydrationAttempt, setHydrationAttempt] = useState(0);
  const resolvedEmailRef = useRef(resolvedEmail);
  resolvedEmailRef.current = resolvedEmail;

  // Send enablement: canonical token signal + in-flight + cooldown — not email display.
  const canResend =
    resendCooldown === 0 && !loadingSendMail && !loadingValidate && isAuthed;
  const canConfirm =
    !loadingValidate && !loadingSendMail && isCompleteOtpCode(code);

  // Unauthenticated (no access token) → login. Already-verified → post-verification matrix.
  useEffect(() => {
    if (!isAuthed) {
      navigate('/login', { replace: true });
      return;
    }
    if (identity?.isEmailVerified) {
      navigate(resolvePostVerificationDestination(userInfo), { replace: true });
    }
  }, [isAuthed, identity, userInfo, navigate]);

  // Bounded route-local email hydration when canonical userInfo omits email.
  useEffect(() => {
    if (!isAuthed) {
      return undefined;
    }

    const fromStore = readCanonicalEmail(userInfo);
    if (fromStore) {
      setResolvedEmail(fromStore);
      setEmailLoadState('ready');
      setEmailLoadError('');
      return undefined;
    }

    if (resolvedEmailRef.current) {
      return undefined;
    }

    const controller = new AbortController();
    let active = true;

    setEmailLoadState('loading');
    setEmailLoadError('');

    api
      .get('/users/profile/', { signal: controller.signal })
      .then(({ data }) => {
        if (!active) return;

        const email = String(data?.email || '').trim();
        if (email) {
          setResolvedEmail(email);
          setEmailLoadState('ready');
          setEmailLoadError('');
        } else {
          setEmailLoadState('error');
          setEmailLoadError(EMAIL_HYDRATION_ERROR);
        }

        // Already-verified: reconcile via action layer (persist + AUTH_IDENTITY_PATCH),
        // then navigate with the same destination helper. No second profile GET.
        if (data?.is_email_verified === true) {
          try {
            const reconciled = dispatch(reconcileUserInfoFromProfile(data));
            if (!active) return;
            navigate(resolvePostVerificationDestination(reconciled), {
              replace: true,
            });
          } catch (_persistError) {
            if (!active) return;
            // Clear local email so retry can re-run bounded hydration.
            setResolvedEmail('');
            resolvedEmailRef.current = '';
            setEmailLoadState('error');
            setEmailLoadError(EMAIL_RECONCILIATION_ERROR);
          }
        }
      })
      .catch((error) => {
        if (!active || isRequestCanceled(error)) return;
        setEmailLoadState('error');
        setEmailLoadError(EMAIL_HYDRATION_ERROR);
      });

    return () => {
      active = false;
      controller.abort();
    };
  }, [hydrationAttempt, isAuthed, userInfo, dispatch, navigate]);

  // Send-code feedback: toast once, start honest cooldown, reset slice so remount
  // does not re-toast or restart the timer.
  useEffect(() => {
    if (errorSendMail) {
      const text = errorSendMail;
      setStatusMessage({ tone: 'error', text, source: 'send' });
      toast.error(text);
      dispatch({ type: USER_SEND_MAIL_PROFILE_RESET });
      return;
    }
    if (successSendMail) {
      const text = 'Код надіслано на вашу пошту';
      setStatusMessage({ tone: 'success', text, source: null });
      setCodeSentOnce(true);
      toast.success(text);
      setResendCooldown(RESEND_COOLDOWN_SECONDS);
      dispatch({ type: USER_SEND_MAIL_PROFILE_RESET });
    }
  }, [errorSendMail, successSendMail, dispatch]);

  // Confirm feedback: navigate only after validateUserProfile has already
  // persisted + patched identity (tokens preserved). Persistence failure never
  // sets success, so this path stays silent and on-page.
  useEffect(() => {
    if (errorValidate) {
      const text = errorValidate;
      setStatusMessage({ tone: 'error', text, source: 'confirm' });
      toast.error(text);
      dispatch({ type: USER_VALIDATE_PROFILE_RESET });
      return;
    }
    if (successValidate) {
      const text = 'Пошту успішно підтверджено!';
      setStatusMessage({ tone: 'success', text, source: null });
      toast.success('Пошта успішно підтверджена!');
      dispatch({ type: USER_VALIDATE_PROFILE_RESET });
      dispatch(getUserDetails('profile'));
      navigate(resolvePostVerificationDestination(userInfo), { replace: true });
    }
  }, [errorValidate, successValidate, userInfo, dispatch, navigate]);

  // Resend cooldown ticker — same pattern as LoginScreen recovery cooldown.
  useEffect(() => {
    if (resendCooldown <= 0) return undefined;
    const id = setInterval(() => {
      setResendCooldown((s) => (s <= 1 ? 0 : s - 1));
    }, 1000);
    return () => clearInterval(id);
  }, [resendCooldown > 0]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (loadingSendMail) {
      setStatusMessage({ tone: 'info', text: 'Надсилаємо код…', source: null });
    }
  }, [loadingSendMail]);

  useEffect(() => {
    if (loadingValidate) {
      setStatusMessage({ tone: 'info', text: 'Перевіряємо код…', source: null });
    }
  }, [loadingValidate]);

  const sendMail = (e) => {
    e.preventDefault();
    if (!canResend) return;
    // Backend identifies the account from the authenticated session.
    dispatch(userSendMail());
  };

  const validateHandler = (e) => {
    e.preventDefault();
    if (loadingValidate || loadingSendMail) return;
    if (!isCompleteOtpCode(code)) {
      const text = 'Перевірте чи ви ввели код правильно.';
      setStatusMessage({ tone: 'error', text, source: 'confirm' });
      toast.error(text);
      return;
    }
    dispatch(validateUserProfile(code));
  };

  const handleCodeChange = (nextCode) => {
    setCode(nextCode);
    if (statusMessage.source === 'confirm' && statusMessage.tone === 'error') {
      setStatusMessage({ tone: 'idle', text: '', source: null });
    }
  };

  const handleLogout = (e) => {
    e.preventDefault();
    dispatch(logout());
  };

  const retryEmailHydration = (e) => {
    e.preventDefault();
    // Retry only reloads the display email — never auto-sends a verification code.
    setEmailLoadError('');
    setEmailLoadState('loading');
    setHydrationAttempt((n) => n + 1);
  };

  let sendLabel = 'Надіслати код';
  if (loadingSendMail) {
    sendLabel = 'Надсилання…';
  } else if (resendCooldown > 0 || codeSentOnce) {
    sendLabel = 'Надіслати код повторно';
  }

  let confirmLabel = 'Підтвердити код';
  if (loadingValidate) {
    confirmLabel = 'Перевірка коду…';
  }

  const showEmailPending = emailLoadState === 'loading';
  const showEmailError = emailLoadState === 'error';
  const showEmailValue = emailLoadState === 'ready' && Boolean(resolvedEmail);
  // Retry is available after a failed attempt, including while a retry is in flight
  // so a newer attempt can abort a superseded request.
  const showEmailRetry = showEmailError || (showEmailPending && hydrationAttempt > 0);

  return (
    <section className="login-section" id="Csection">
      <div className="auth-shell">
        <div className="auth-card email-verification-card">
          <header className="auth-card__head email-verification-head">
            <p
              className="email-verification-step"
              data-testid="email-verification-step"
            >
              Крок 1 з 2
            </p>
            <h1 className="auth-card__title">Підтвердження пошти</h1>
            <p className="auth-card__subtitle email-verification-lead">
              Підтвердіть email перед завершенням профілю. Це обовʼязковий перший
              крок після реєстрації або входу з непідтвердженою поштою.
            </p>
          </header>

          <div
            className="email-verification-content"
            data-testid="email-verification-content"
          >
            <div
              className="email-verification-email"
              data-testid="email-verification-email"
            >
              <span className="email-verification-email__label">Ваш Email</span>
              {showEmailPending && (
                <span
                  className="email-verification-email__value email-verification-email__value--pending"
                  data-testid="email-verification-email-pending"
                >
                  Завантаження адреси…
                </span>
              )}
              {showEmailValue && (
                <strong
                  className="email-verification-email__value"
                  data-testid="email-verification-email-value"
                >
                  {resolvedEmail}
                </strong>
              )}
              {showEmailError && (
                <span
                  className="email-verification-email__value email-verification-email__value--pending"
                  data-testid="email-verification-email-error"
                  role="alert"
                >
                  {emailLoadError}
                </span>
              )}
              {showEmailRetry && (
                <button
                  type="button"
                  className="email-verification-logout"
                  onClick={retryEmailHydration}
                  data-testid="email-verification-email-retry"
                >
                  Повторити
                </button>
              )}
            </div>

            <div
              className={`email-verification-status email-verification-status--${statusMessage.tone}`}
              role="status"
              aria-live="polite"
              aria-atomic="true"
              data-testid="email-verification-status"
            >
              {statusMessage.text}
            </div>

            <ol className="email-verification-flow" aria-label="Кроки підтвердження">
              <li className="email-verification-flow__step">
                <form
                  className="email-verification-form email-verification-form--send"
                  onSubmit={sendMail}
                  noValidate
                >
                  <div className="email-verification-form__head">
                    <span className="email-verification-flow__num" aria-hidden="true">
                      1
                    </span>
                    <span className="email-verification-flow__label">
                      Надішліть код на пошту
                    </span>
                  </div>
                  <button
                    className="action-btn action-btn--accent email-verification-send"
                    type="submit"
                    disabled={!canResend}
                    aria-busy={loadingSendMail ? 'true' : 'false'}
                    data-testid="email-verification-send"
                  >
                    {sendLabel}
                  </button>
                  {resendCooldown > 0 && (
                    <p
                      className="email-verification-cooldown"
                      data-testid="email-verification-cooldown"
                    >
                      Повторне надсилання через{' '}
                      <strong>{resendCooldown}с</strong>
                    </p>
                  )}
                </form>
              </li>

              <li className="email-verification-flow__step">
                <form
                  className="email-verification-form email-verification-form--confirm"
                  onSubmit={validateHandler}
                  noValidate
                >
                  <div className="email-verification-form__head">
                    <span className="email-verification-flow__num" aria-hidden="true">
                      2
                    </span>
                    <span className="email-verification-flow__label">
                      Введіть код і підтвердіть
                    </span>
                  </div>
                  <label
                    className="email-verification-code-label"
                    htmlFor="email-verification-code-input"
                  >
                    Код підтвердження
                  </label>
                  <OtpCodeInput
                    id="email-verification-code-input"
                    className="ui-text-input email-verification-code"
                    value={code}
                    onChange={handleCodeChange}
                    placeholder="Наприклад, 012345"
                    disabled={loadingValidate}
                    aria-invalid={
                      statusMessage.source === 'confirm' && statusMessage.tone === 'error'
                        ? 'true'
                        : undefined
                    }
                    data-testid="email-verification-code"
                  />
                  <button
                    className="action-btn action-btn--accent email-verification-confirm"
                    type="submit"
                    disabled={!canConfirm}
                    aria-busy={loadingValidate ? 'true' : 'false'}
                    data-testid="email-verification-confirm"
                  >
                    {confirmLabel}
                  </button>
                </form>
              </li>
            </ol>

            <div className="email-verification-footer">
              <button
                type="button"
                className="email-verification-logout"
                onClick={handleLogout}
                data-testid="email-verification-logout"
              >
                Вийти / змінити акаунт
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

export default ProfileEmailValidateScreen;
